import SiteHeader from "@/components/SiteHeader";
import { PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";

const UPDATED = "July 16, 2026";

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-bold text-slate-900 mb-3">
      <span className="text-brand font-mono mr-2">{String(n).padStart(2, "0")}</span>{title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Legal</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900">Privacy Policy</h1>
        <p className="mt-4 text-slate-500">Last updated: {UPDATED}</p>

        <div className="mt-12">
          <Section n={1} title="Introduction">
            <p>
              This Privacy Policy explains how {STRATEGIST.business} ("we", "us", "our") collects, uses and protects
              your information when you use {PROGRAM_NAME} (the "Service"). By using the Service, you agree to the
              practices described here.
            </p>
          </Section>

          <Section n={2} title="Information We Collect">
            <p>We only collect information you choose to give us or that is needed to run the Service, including:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Contact details</strong> you submit — such as your name, email, phone number, and the equipment or lanes you run — when you request a strategy session or complete the readiness diagnostic.</li>
              <li><strong>Diagnostic and tool inputs</strong> — your quiz answers, cost-per-mile figures and progress, used to generate your results.</li>
              <li><strong>Account information</strong> — your email and a securely hashed password if you create a paid account.</li>
              <li><strong>Basic usage data</strong> — needed to keep the Service working and secure.</li>
            </ul>
          </Section>

          <Section n={3} title="How We Use Your Information">
            <p>We use your information only to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>respond to your request and contact you about a lane strategy session or inquiry you submitted;</li>
              <li>provide, personalize and operate the Service (e.g., generating your diagnostic score or CPM results);</li>
              <li>process your subscription if you upgrade to the paid tier; and</li>
              <li>keep the Service secure and comply with the law.</li>
            </ul>
          </Section>

          <Section n={4} title="We Do Not Sell Your Information">
            <p>
              <strong>We do not sell, rent, or trade your personal information to any third party — ever.</strong> We
              will <strong>not</strong> use your information to market or offer you other goods or services outside of
              {" "}{PROGRAM_NAME} / {STRATEGIST.business}. The only communications you'll receive from us relate to the
              request you submitted or your account with us.
            </p>
          </Section>

          <Section n={5} title="Third-Party &amp; External Links (FMCSA, ELD &amp; Partner Resources)">
            <p>
              The Service links out to third-party websites and resources — for example government sites like
              <strong> FMCSA</strong>, and equipment, insurance, factoring or ELD providers and other partners (some of
              which may be affiliate links).
            </p>
            <p>
              <strong>Important:</strong> once you click through to one of these external sites, you are on <em>their</em>
              {" "}website — not ours. If you enter any information on those sites (such as an application, account
              signup, or purchase), <strong>that information is collected and used by that third party under their own
              privacy policy and terms, not ours.</strong> We do not control, and are not responsible for, how those
              third parties handle your information. We encourage you to review each site's privacy policy before
              entering any personal or business details.
            </p>
          </Section>

          <Section n={6} title="Service Providers We Use">
            <p>
              We use a small number of trusted providers strictly to operate the Service — for example, a payment
              processor (Stripe) to handle subscriptions, and an email provider to deliver messages. These providers
              process data only on our behalf to perform their function and are not permitted to use it for their own
              marketing. We do not store your full payment card details; those are handled securely by Stripe.
            </p>
          </Section>

          <Section n={7} title="Data Retention">
            <p>
              We keep your information only as long as needed to respond to your request, operate your account, and meet
              legal or record-keeping obligations. You may ask us to delete your information at any time using the
              contact below.
            </p>
          </Section>

          <Section n={8} title="Security">
            <p>
              We take reasonable measures to protect your information, including hashing account passwords and limiting
              access. No method of transmission or storage is 100% secure, but we work to safeguard your data.
            </p>
          </Section>

          <Section n={9} title="Your Choices">
            <p>
              You can request access to, correction of, or deletion of your personal information, and you can ask us to
              stop contacting you at any time. Just reach out using the contact details below.
            </p>
          </Section>

          <Section n={10} title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Material changes will be reflected by updating the
              "Last updated" date above. Continued use of the Service after changes take effect constitutes acceptance
              of the revised policy.
            </p>
          </Section>

          <Section n={11} title="Contact">
            <p>
              Questions or privacy requests? Contact {STRATEGIST.name}, {STRATEGIST.title}, {STRATEGIST.business}:
              {" "}
              <a href={`mailto:${STRATEGIST.email}`} data-testid="privacy-contact-email" className="text-brand font-bold hover:underline break-all">{STRATEGIST.email}</a>.
            </p>
          </Section>
        </div>

        <div className="border-t border-slate-200 pt-8 mt-4 text-sm text-slate-400">
          © {new Date().getFullYear()} {STRATEGIST.business}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
